import { useEffect, useRef, useState } from "react";
import { Spinner } from "@heroui/react";
import {
  api,
  Improvement,
  ImprovementHistoryEntry,
  ImprovementHistoryResponse,
} from "../api/client";

export function Improvements() {
  const [items, setItems] = useState<Improvement[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [historyVersion, setHistoryVersion] = useState(0);

  const DELETE_GRACE_MS = 5000;
  const pendingDeletes = useRef<Improvement[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  async function load() {
    const data = await api.get<Improvement[]>("/improvements");
    setItems(data);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    function onChanged() { load(); }
    window.addEventListener("cookmenu:data-changed", onChanged);
    return () => window.removeEventListener("cookmenu:data-changed", onChanged);
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setText("");
    const item = await api.post<Improvement>("/improvements", { text: trimmed });
    setItems((prev) => [...prev, item]);
  }

  function scheduleFlush() {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushDeletes, DELETE_GRACE_MS);
  }

  function flushDeletes() {
    if (flushTimer.current) { clearTimeout(flushTimer.current); flushTimer.current = null; }
    const toDelete = pendingDeletes.current;
    pendingDeletes.current = [];
    setPendingCount(0);
    toDelete.forEach((it) => { api.del(`/improvements/${it.id}`).catch(() => load()); });
  }

  function undoDeletes() {
    if (flushTimer.current) { clearTimeout(flushTimer.current); flushTimer.current = null; }
    const restored = pendingDeletes.current;
    pendingDeletes.current = [];
    setPendingCount(0);
    setItems((cur) => [...cur, ...restored].sort((a, b) => a.position - b.position));
  }

  function discard(item: Improvement) {
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    pendingDeletes.current = [...pendingDeletes.current, item];
    setPendingCount(pendingDeletes.current.length);
    scheduleFlush();
  }

  useEffect(() => {
    return () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      pendingDeletes.current.forEach((it) => api.del(`/improvements/${it.id}`).catch(() => {}));
    };
  }, []);

  async function complete(item: Improvement) {
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    try {
      await api.post(`/improvements/${item.id}/complete`);
      setHistoryVersion((v) => v + 1);
    } catch {
      load();
    }
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Millores</h1>
          <div className="sub">{items.length} pendents</div>
        </div>
      </div>

      <div className="sh-grid">
        {/* Columna principal */}
        <div>
          {/* Formulari d'afegir */}
          <form onSubmit={add} className="flex gap-2 mb-5">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Proposa una millora…"
              aria-label="Nova millora"
              className="nm-input flex-1"
            />
            <button
              type="submit"
              disabled={!text.trim()}
              className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Afegeix
            </button>
          </form>

          {loading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : items.length === 0 ? (
            <div className="empty" style={{ minHeight: 140 }}>
              <b>Cap millora pending</b>
              <span className="muted">Afegeix idees per millorar l'aplicació</span>
            </div>
          ) : (
            <div className="card" style={{ overflow: "hidden" }}>
              {items.map((item, i) => (
                <div key={item.id} style={{ borderTop: i ? "1px solid var(--border)" : 0 }}>
                  <ImprovementRow
                    item={item}
                    onComplete={complete}
                    onDiscard={discard}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: històric */}
        <aside>
          <HistoryPanel version={historyVersion} />
        </aside>
      </div>

      {/* Snackbar desfés */}
      {pendingCount > 0 && (
        <div className="snackbar-bottom fixed inset-x-0 z-50 flex justify-center px-4">
          <div className="flex items-center gap-3 rounded-full border border-border bg-surface px-4 py-2 text-sm shadow-card-md">
            <span className="text-foreground">
              {pendingCount} millora{pendingCount !== 1 ? "s" : ""} descartada{pendingCount !== 1 ? "s" : ""}
            </span>
            <button type="button" onClick={undoDeletes} className="font-bold text-accent hover:underline">
              Desfés
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImprovementRow({
  item,
  onComplete,
  onDiscard,
}: {
  item: Improvement;
  onComplete: (i: Improvement) => void;
  onDiscard: (i: Improvement) => void;
}) {
  return (
    <div className="sh-row" style={{ alignItems: "flex-start", gap: 10, paddingBlock: "12px" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="sh-name" style={{ whiteSpace: "normal", lineHeight: 1.45 }}>{item.text}</div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
          <span
            className="av"
            style={{ background: item.authorColor, width: 22, height: 22, fontSize: 12, flexShrink: 0 }}
            aria-hidden
          >
            {item.authorEmoji}
          </span>
          {item.authorName}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        {/* Executar */}
        <button
          type="button"
          onClick={() => onComplete(item)}
          aria-label="Marca com a realitzada"
          title="Realitzada"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-muted transition-colors hover:bg-success-soft hover:text-success"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
        {/* Descartar */}
        <button
          type="button"
          onClick={() => onDiscard(item)}
          aria-label="Descarta"
          title="Descarta"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-muted transition-colors hover:bg-danger-soft hover:text-danger"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function HistoryPanel({ version }: { version: number }) {
  const PAGE = 20;
  const [entries, setEntries] = useState<ImprovementHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  async function loadPage(skip: number, replace: boolean) {
    const res = await api.get<ImprovementHistoryResponse>(
      `/improvements/history?skip=${skip}&take=${PAGE}`
    );
    setTotal(res.total);
    setEntries((prev) => (replace ? res.entries : [...prev, ...res.entries]));
  }

  useEffect(() => {
    setLoading(true);
    loadPage(0, true).finally(() => setLoading(false));
  }, [version]);

  async function loadMore() {
    setLoadingMore(true);
    await loadPage(entries.length, false).finally(() => setLoadingMore(false));
  }

  return (
    <div>
      <div className="section-title mb-3">Millores realitzades</div>

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted py-8 text-center">
          Encara no s'ha realitzat cap millora.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {entries.map((h) => (
            <div key={h.id} className="card" style={{ padding: "12px 16px" }}>
              <div className="text-sm" style={{ lineHeight: 1.45 }}>{h.text}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                <span
                  className="av"
                  style={{ background: h.authorColor, width: 20, height: 20, fontSize: 11, flexShrink: 0 }}
                  aria-hidden
                >
                  {h.authorEmoji}
                </span>
                {h.authorName} · {formatDate(h.completedAt)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && entries.length < total && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="mt-3 w-full rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-muted transition-colors hover:bg-surface-secondary disabled:opacity-50"
        >
          {loadingMore ? "Carregant…" : "Carrega'n més"}
        </button>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("ca-ES", {
    timeZone: "Europe/Madrid",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
